import type { ConversationPropertiesResponseDatagramBuf } from "~/classes/generated/response"

import { StaticConversation } from "~/classes/conversation/StaticConversation"
import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { ConversationPropertiesRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { parseConversation } from "~/utils/flatbuffers/conversation"
import { addRequestIdToBuf, parseDatagramError, parseRequestId } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf } from "~/utils/flatbuffers/entity"
import { addConversationIdToBuf, parseConversationId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("conversation/properties")

export function sendConversationPropertiesRequests(requests: ConversationPropertiesRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const propertiesRequests = []
  for (const request of requests) {
    const selection = addEntityPropertySelectionToBuf(builder, request.selection)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const conversationId = addConversationIdToBuf(builder, request.conversationId)
    ConversationPropertiesRequestDatagramBuf.startConversationPropertiesRequestDatagramBuf(builder)
    ConversationPropertiesRequestDatagramBuf.addRequestId(builder, requestId)
    ConversationPropertiesRequestDatagramBuf.addOffline(builder, request.offline)
    ConversationPropertiesRequestDatagramBuf.addConversationId(builder, conversationId)
    ConversationPropertiesRequestDatagramBuf.addSelection(builder, selection)
    const propertiesRequest = ConversationPropertiesRequestDatagramBuf.endConversationPropertiesRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addConversationPropertiesRequest(builder, propertiesRequest)
    propertiesRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const properties = DatagramBuf.createRequestVector(builder, propertiesRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, properties)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleConversationPropertiesResponseDatagramBuf(datagram: ConversationPropertiesResponseDatagramBuf) {
  onConversationPropertiesResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    conversation: maybe(parseConversation)(datagram.conversation()),
    offline: datagram.offline(),
    conversationId: parseConversationId(datagram.conversationId()!),
  })
}

export function onConversationPropertiesResponse(response: ConversationPropertiesResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received conversation properties", response)

  emitInterfaceEvent("conversationPropertiesResponse", response, response.requestId)

  if (response.error)
    return onConversationPropertiesErrored(response.error, response.offline)
  if (!response.conversation)
    throw new Error("No conversation in properties response")

  const iConversation = new StaticConversation(response.conversation)
  emitEntityEvent("conversationProperties", iConversation.id, iConversation.inner(), response.offline)
}

function onConversationPropertiesErrored(error: DatagramError, offline: boolean) {
  const { notifyDatagramError } = useAlerts()

  if (error.type === DatagramErrorTypeBuf.ConversationNotFound && offline)
    return console.debug("Offline conversation not found, waiting for server", error)

  return notifyDatagramError(error, "Conversation properties request was unsuccessful")
}
